import { UserListItem } from '../../../apis/user-management/user-management-api.def.ts'
import { ColDef } from 'ag-grid-community'
import { IdeyaAgGridTable } from '../../../common/ag-grid/ideya-ag-grid-table.tsx'
import { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-enterprise'
import { useAppSelector } from '../../../redux/hooks.ts'
import { useMemo, useRef } from 'react'
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react'
import { USER_MANAGEMENT_COLUMN_STATE_KEY } from '../../../utils/constants.ts'
import { marketDateTimezoneFormatter } from '../../../utils/formatters.ts'
import { selectUser } from '../../../features/user/user.slice.ts'
import { CalendarOutlined, EditOutlined, MailOutlined } from '@ant-design/icons'
import { IdeyaButton } from '../../../common/antd/ideya-button.tsx'
import { selectManagementInput } from '../../../redux/slices/management-input.slice.ts'
import { AccountType } from '../../../enums/management-input.enum.ts'
import {
  canCreateUserPermissions,
  hasPermission,
} from '../../../utils/permissions.ts'
import ReactDOMServer from 'react-dom/server'
import { useTranslation } from 'react-i18next'
import { IsTrialCellRenderer } from '../../components/is-trial-cell-renderer.tsx'

interface UserManagementTableProps {
  isLoading: boolean
  isFetching: boolean
  userList: UserListItem[]
  onEditUser: (id: string) => void
  onInviteUser: (id: string) => void
  onAddBlockLeave: (id: string) => void
  onEditBlockLeave: (id: string) => void
  height: number
}

export const UserManagementTable: React.FC<UserManagementTableProps> = ({
  isLoading,
  isFetching,
  userList,
  onEditUser,
  onInviteUser,
  onAddBlockLeave,
  onEditBlockLeave,
  height,
}) => {
  const gridRef = useRef<AgGridReact>(null)
  const user = useAppSelector(selectUser)
  const managementInput = useAppSelector(selectManagementInput)
  const { t } = useTranslation()

  const canEditUser = hasPermission(
    canCreateUserPermissions,
    user.firmRole,
    user.groupRole,
    user.role
  )

  const columns: ColDef[] = useMemo(() => {
    const baseColumns: ColDef[] = [
      {
        headerName: '',
        field: 'id',
        width: 50,
        cellRenderer: (props: CustomCellRendererProps) => {
          if (
            props.data.createAdminId === user?.sub ||
            user?.role === 'Master'
          ) {
            return (
              <IdeyaButton
                icon={<EditOutlined />}
                onClick={() => onEditUser(props.data.id)}
              />
            )
          } else {
            return <></>
          }
        },
      },
      {
        headerName: '',
        field: 'isTrial',
        suppressHeaderMenuButton: true,
        cellRenderer: IsTrialCellRenderer,
        width: 80,
      },
      {
        headerName: t('user_management.user_table.columns.full_name'),
        field: 'fullName',
      },
      {
        headerName: t('user_management.user_table.columns.email'),
        field: 'email',
      },
      {
        headerName: t('user_management.user_table.columns.firm'),
        field: 'firm.name',
      },
      {
        headerName: t('user_management.user_table.columns.firm_role'),
        field: 'firmRole',
      },
      {
        headerName: t('user_management.user_table.columns.group'),
        field: 'group.name',
      },
      {
        headerName: t('user_management.user_table.columns.group_role'),
        field: 'groupRole',
      },
      {
        headerName: t('user_management.user_table.columns.state'),
        field: 'status',
      },
      {
        headerName: t('user_management.user_table.columns.primary_book'),
        field: 'primaryBook.bookName',
      },
      {
        headerName: t('user_management.user_table.columns.account_type'),
        field: 'accountType',
      },
      {
        headerName: t('user_management.user_table.columns.last_login_time'),
        field: 'lastLoginTime',
        valueFormatter: (params) =>
          marketDateTimezoneFormatter(
            {
              ...params,
              data: {
                ...params.data,
                marketTimeZone:
                  // Browser timezone
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
              },
            },
            { showTimezone: true }
          ),
      },
      {
        headerName: t('user_management.user_table.columns.last_idea_send_time'),
        field: 'lastIdeaSendTime',
        valueFormatter: (params) =>
          marketDateTimezoneFormatter(
            {
              ...params,
              data: {
                ...params.data,
                marketTimeZone:
                  // Browser timezone
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
              },
            },
            { showTimezone: true }
          ),
      },
    ]

    // Conditionally add columns
    if (managementInput.accountType === AccountType.External && canEditUser) {
      const isEmailIndex = baseColumns.findIndex(
        (column) => column.field === 'email'
      )
      baseColumns.splice(isEmailIndex + 1, 0, {
        headerName: 'Send Invite Email',
        field: 'sendInviteEmail',
        cellRenderer: (props: CustomCellRendererProps) => {
          if (
            props.data.createAdminId === user?.sub ||
            user?.role === 'Master'
          ) {
            return (
              <IdeyaButton
                icon={<MailOutlined />}
                onClick={() => onInviteUser(props.data.id)}
              />
            )
          }
          return <></>
        },
      })
    }

    if (managementInput.accountType === AccountType.Internal) {
      const groupRoleIndex = baseColumns.findIndex(
        (column) => column.field === 'groupRole'
      )
      baseColumns.splice(groupRoleIndex + 1, 0, {
        headerName: 'Internal Role',
        field: 'internalRole',
      })
    }

    const savedState = localStorage.getItem(USER_MANAGEMENT_COLUMN_STATE_KEY)
    if (savedState) {
      try {
        const state = JSON.parse(savedState)
        const colOrder = state.map((col: { colId: string }) => col.colId)
        const ordered = colOrder
          .map((id: string) => baseColumns.find((col) => col.field === id))
          .filter(Boolean)
        const missing = baseColumns.filter(
          (col) => !colOrder.includes(col.field)
        )
        return [...ordered, ...missing]
      } catch {
        return baseColumns
      }
    }

    return baseColumns
  }, [
    t,
    managementInput.accountType,
    canEditUser,
    user,
    onEditUser,
    onInviteUser,
  ])

  const handleGridReady = () => {
    if (!gridRef.current) return
    const savedState = localStorage.getItem(USER_MANAGEMENT_COLUMN_STATE_KEY)
    if (savedState) {
      const state = JSON.parse(savedState)
      gridRef.current.api.applyColumnState({
        state: state,
        applyOrder: true,
      })
    }
  }

  const handleColumnChanged = () => {
    if (!gridRef.current) return
    const state = gridRef.current.api.getColumnState()
    localStorage.setItem(
      USER_MANAGEMENT_COLUMN_STATE_KEY,
      JSON.stringify(state)
    )
  }

  const handleSortChanged = () => {
    if (!gridRef.current) return
    const state = gridRef.current.api.getColumnState()
    localStorage.setItem(
      USER_MANAGEMENT_COLUMN_STATE_KEY,
      JSON.stringify(state)
    )
  }

  const getContextMenuItems = (params: GetContextMenuItemsParams) => {
    const items: MenuItemDef[] = []

    if (params.node && params.node.data) {
      items.push({
        name: 'Add Block Leave',
        action: () => {
          onAddBlockLeave(params.node?.data.id)
        },
        icon: ReactDOMServer.renderToString(<CalendarOutlined />),
      })

      items.push({
        name: 'Edit Block Leave',
        action: () => {
          onEditBlockLeave(params.node?.data.id)
        },
        icon: ReactDOMServer.renderToString(<EditOutlined />),
      })
    }

    return items
  }

  return (
    <IdeyaAgGridTable
      ref={gridRef}
      columns={columns}
      rowData={userList}
      loading={isLoading || isFetching}
      onGridReady={handleGridReady}
      onColumnMoved={handleColumnChanged}
      onColumnPinned={handleColumnChanged}
      onColumnVisible={handleColumnChanged}
      onSortChanged={handleSortChanged}
      getContextMenuItems={getContextMenuItems}
      height={height}
    />
  )
}
